import type { Identifier, XYCoord } from 'dnd-core';
import { useContext, useMemo, useRef } from 'react';
import { SwitchThemeContext } from '@tryhackme/thm-ui-components';
import { CSSTransition } from 'react-transition-group';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { useDrag, useDrop } from 'react-dnd';
import { yupResolver } from '@hookform/resolvers/yup';
import { ThemeColorMode } from 'src/common/enums';
import { useForm, FormProvider } from 'src/common/form';
import { TaskForm } from '../task-form';
import { TaskValidation } from '../../task-tab.constants';
import { StyledAccordionSummary, StyledAccordionWrapper, StyledChevronIcon, StyledGripIcon, StyledGripIconContainer, StyledHeaderContentContainer, StyledTaskAccordionDetails, StyledTaskNumber, StyledTaskTitle } from './task-accordion.styles';
import { TaskAccordionProps } from './task-accordion.types';
import { ACCORDION_ANIMATION_DURATION } from './task-accordion.constants';
export const TaskAccordion = ({
  taskNo,
  activeAccordion,
  setActiveAccordion,
  taskMaterialData,
  task,
  roomId,
  handleRemoveTask,
  handleMoveTask,
  findTask,
  index,
  fieldId,
  handleTaskOrderUpdate,
  roomDetails,
  isContentDev
}: TaskAccordionProps) => {
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const dragRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isOpen = useMemo(() => activeAccordion === taskNo, [activeAccordion, taskNo]);
  const methods = useForm({
    resolver: yupResolver(TaskValidation),
    defaultValues: task,
    mode: 'all'
  });
  const [{
    handlerId
  }, drop] = useDrop<{
    id: string;
    index: number;
  }, void, {
    handlerId: Identifier | null;
  }>({
    accept: 'task',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item, monitor) {
      if (!dragRef.current) {
        return;
      }
      const dragIndex = findTask(item.id);
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = dragRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      handleMoveTask(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
    drop: () => {
      handleTaskOrderUpdate();
    }
  });
  const [{
    isDragging
  }, drag] = useDrag({
    type: 'task',
    item: () => ({
      id: fieldId,
      index
    }),
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: monitor => {
      const inDrag = monitor.isDragging();
      if (activeAccordion || inDrag) return false;
      return true;
    }
  });
  drag(drop(dragRef));
  return <StyledAccordionWrapper isOpen={Boolean(activeAccordion)} isDragging={isDragging} $isDark={isDarkMode} data-handler-id={handlerId}
  // only attach dragRef when no accordion is open, To support FireFox.
  // since Firefox do not let us select text inside input and text area
  // when draggable is true.
  {...!activeAccordion && {
    ref: dragRef
  }} data-sentry-element="StyledAccordionWrapper" data-sentry-component="TaskAccordion" data-sentry-source-file="task-accordion.tsx">
      <StyledAccordionSummary onClick={() => setActiveAccordion(taskNo)} id={`header-${taskNo}`} data-testid={`header-${taskNo}`} role="button" aria-controls={`content-${taskNo}`} aria-expanded={isOpen} tabIndex={taskNo} $isOpen={isOpen} data-sentry-element="StyledAccordionSummary" data-sentry-source-file="task-accordion.tsx">
        <StyledHeaderContentContainer data-sentry-element="StyledHeaderContentContainer" data-sentry-source-file="task-accordion.tsx">
          <StyledTaskNumber data-sentry-element="StyledTaskNumber" data-sentry-source-file="task-accordion.tsx">{`Task No ${index + 1}`}</StyledTaskNumber>
          <StyledTaskTitle dangerouslySetInnerHTML={{
          __html: task.title
        }} data-sentry-element="StyledTaskTitle" data-sentry-source-file="task-accordion.tsx" />
        </StyledHeaderContentContainer>

        <StyledChevronIcon data-testid={`chevron-${taskNo}`} aria-hidden={!isOpen} $isOpen={isOpen} icon={faAngleDown} data-sentry-element="StyledChevronIcon" data-sentry-source-file="task-accordion.tsx" />
      </StyledAccordionSummary>
      <CSSTransition nodeRef={contentRef} in={isOpen} timeout={ACCORDION_ANIMATION_DURATION} classNames="accordion-details" unmountOnExit data-sentry-element="CSSTransition" data-sentry-source-file="task-accordion.tsx">
        <div ref={contentRef}>
          <StyledTaskAccordionDetails role="region" id={`content-${taskNo}`} data-testid={`content-${taskNo}`} aria-labelledby={`header-${taskNo}`} $isDark={isDarkMode} data-sentry-element="StyledTaskAccordionDetails" data-sentry-source-file="task-accordion.tsx">
            <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-source-file="task-accordion.tsx">
              <TaskForm taskNo={taskNo} taskMaterialData={taskMaterialData} roomId={roomId} handleRemoveTask={handleRemoveTask} roomDetails={roomDetails} isContentDev={isContentDev} data-sentry-element="TaskForm" data-sentry-source-file="task-accordion.tsx" />
            </FormProvider>
          </StyledTaskAccordionDetails>
        </div>
      </CSSTransition>

      <StyledGripIconContainer data-sentry-element="StyledGripIconContainer" data-sentry-source-file="task-accordion.tsx">
        <StyledGripIcon icon={faGripDotsVertical} data-sentry-element="StyledGripIcon" data-sentry-source-file="task-accordion.tsx" />
      </StyledGripIconContainer>
    </StyledAccordionWrapper>;
};